import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, Clock, ImageIcon, Loader2, Palette, Save, Upload } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/app/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card'
import { Input } from '@/app/components/ui/input'
import { settingsRecord, settingsService, settingValue, type ImageSettingValue } from '@/app/services/settings.service'

const COLOR_KEYS = [
    { key: 'theme.primary_color', label: 'Primario' },
    { key: 'theme.secondary_color', label: 'Secundario' },
    { key: 'theme.accent_color', label: 'Acento' },
] as const

const IMAGE_KEYS = [
    { key: 'logo_main', label: 'Logo principal' },
    { key: 'logo_email', label: 'Logo correos' },
    { key: 'favicon', label: 'Favicon' },
] as const

const TIMEZONES = [
    'America/Mexico_City',
    'America/Monterrey',
    'America/Cancun',
    'America/Chihuahua',
    'America/Hermosillo',
    'America/Tijuana',
]

type ImageKey = typeof IMAGE_KEYS[number]['key']

interface FormState {
    company_name: string
    timezone: string
    'theme.primary_color': string
    'theme.secondary_color': string
    'theme.accent_color': string
}

const DEFAULT_FORM: FormState = {
    company_name: '',
    timezone: 'America/Mexico_City',
    'theme.primary_color': '#3B7D3B',
    'theme.secondary_color': '#2563EB',
    'theme.accent_color': '#F59E0B',
}

export function PlatformSettings() {
    const queryClient = useQueryClient()
    const [form, setForm] = useState<FormState>(DEFAULT_FORM)
    const [savedForm, setSavedForm] = useState<FormState>(DEFAULT_FORM)
    const [uploadingKey, setUploadingKey] = useState<ImageKey | null>(null)

    const { data: settings = [], isLoading } = useQuery({
        queryKey: ['settings'],
        queryFn: settingsService.getSettings,
    })

    const settingMap = useMemo(() => settingsRecord(settings), [settings])

    useEffect(() => {
        if (!settings.length) {
            return
        }

        const nextForm = {
            company_name: settingValue(settings, 'company_name', '') || '',
            timezone: settingValue(settings, 'timezone', 'America/Mexico_City'),
            'theme.primary_color': settingValue(settings, 'theme.primary_color', '#3B7D3B'),
            'theme.secondary_color': settingValue(settings, 'theme.secondary_color', '#2563EB'),
            'theme.accent_color': settingValue(settings, 'theme.accent_color', '#F59E0B'),
        }

        setForm(nextForm)
        setSavedForm(nextForm)
    }, [settings])

    const updateMutation = useMutation({
        mutationFn: settingsService.updateSettings,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['settings'] })
            toast.success('Configuracion actualizada')
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message ?? 'No se pudo guardar la configuracion')
        },
    })

    const uploadMutation = useMutation({
        mutationFn: ({ key, file }: { key: ImageKey; file: File }) => settingsService.uploadAsset(key, file),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['settings'] })
            toast.success('Imagen actualizada')
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message ?? 'No se pudo subir la imagen')
        },
        onSettled: () => setUploadingKey(null),
    })

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const nextForm = {
            ...form,
            company_name: form.company_name.trim(),
        }
        const payload = changedSettingsPayload(nextForm, savedForm)

        if (Object.keys(payload).length === 0) {
            toast.info('No hay cambios por guardar')
            return
        }

        try {
            await updateMutation.mutateAsync(payload)
            setForm(nextForm)
            setSavedForm(nextForm)
        } catch {
            // El toast se emite en onError.
        }
    }

    const handleImageChange = async (key: ImageKey, file?: File) => {
        if (!file) {
            return
        }

        setUploadingKey(key)

        try {
            await uploadMutation.mutateAsync({ key, file })
        } catch {
            // El toast se emite en onError.
        }
    }

    return (
        <div className="min-h-full bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
            <div className="mx-auto flex max-w-6xl flex-col gap-6">
                <div className="flex flex-col gap-2">
                    <h1 className="text-2xl font-semibold text-slate-900">Configuracion de plataforma</h1>
                    <p className="text-sm text-slate-500">
                        Ajusta la identidad visual que usa el punto de venta y las pantallas de acceso.
                    </p>
                </div>

                {isLoading ? (
                    <div className="flex min-h-64 items-center justify-center rounded-lg border border-slate-200 bg-white">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
                        <div className="space-y-6">
                            <Card className="rounded-lg">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Building2 className="h-5 w-5 text-primary" />
                                        Identidad
                                    </CardTitle>
                                    <CardDescription>Nombre visible y recursos principales del POS.</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-5">
                                    <div className="space-y-2">
                                        <label htmlFor="company_name" className="text-sm font-medium text-slate-700">
                                            Nombre comercial
                                        </label>
                                        <Input
                                            id="company_name"
                                            value={form.company_name}
                                            onChange={(event) => setForm((current) => ({
                                                ...current,
                                                company_name: event.target.value,
                                            }))}
                                            placeholder="Farmacias Salud Plus"
                                        />
                                    </div>

                                    <div className="grid gap-4 md:grid-cols-3">
                                        {IMAGE_KEYS.map(({ key, label }) => {
                                            const image = settingMap[key]?.value as ImageSettingValue | null | undefined
                                            const isUploading = uploadingKey === key

                                            return (
                                                <div key={key} className="rounded-lg border border-slate-200 bg-white p-4">
                                                    <div className="flex h-24 items-center justify-center rounded-md border border-dashed border-slate-200 bg-slate-50">
                                                        {image?.url ? (
                                                            <img src={image.url} alt={label} className="max-h-20 max-w-full object-contain" />
                                                        ) : (
                                                            <ImageIcon className="h-8 w-8 text-slate-300" />
                                                        )}
                                                    </div>
                                                    <div className="mt-3 space-y-2">
                                                        <p className="text-sm font-medium text-slate-800">{label}</p>
                                                        <label className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50">
                                                            {isUploading ? (
                                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                            ) : (
                                                                <Upload className="h-4 w-4" />
                                                            )}
                                                            Subir
                                                            <input
                                                                type="file"
                                                                accept="image/png,image/jpeg,image/webp"
                                                                className="sr-only"
                                                                disabled={isUploading}
                                                                onClick={(event) => {
                                                                    event.currentTarget.value = ''
                                                                }}
                                                                onChange={(event) => handleImageChange(key, event.target.files?.[0])}
                                                            />
                                                        </label>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="rounded-lg">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Palette className="h-5 w-5 text-primary" />
                                        Paleta de colores
                                    </CardTitle>
                                    <CardDescription>Estos colores se aplican al tema activo del POS.</CardDescription>
                                </CardHeader>
                                <CardContent className="grid gap-4 md:grid-cols-3">
                                    {COLOR_KEYS.map(({ key, label }) => (
                                        <ColorField
                                            key={key}
                                            label={label}
                                            value={form[key]}
                                            onChange={(value) => setForm((current) => ({ ...current, [key]: value }))}
                                        />
                                    ))}
                                </CardContent>
                            </Card>
                        </div>

                        <div className="space-y-6">
                            <Card className="rounded-lg">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Clock className="h-5 w-5 text-primary" />
                                        Regional
                                    </CardTitle>
                                    <CardDescription>Configuraciones base para la compania.</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    <label htmlFor="timezone" className="text-sm font-medium text-slate-700">
                                        Zona horaria
                                    </label>
                                    <select
                                        id="timezone"
                                        value={form.timezone}
                                        onChange={(event) => setForm((current) => ({
                                            ...current,
                                            timezone: event.target.value,
                                        }))}
                                        className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                                    >
                                        {TIMEZONES.map((timezone) => (
                                            <option key={timezone} value={timezone}>
                                                {timezone}
                                            </option>
                                        ))}
                                    </select>
                                </CardContent>
                            </Card>

                            <Card className="rounded-lg">
                                <CardHeader>
                                    <CardTitle className="text-base">Vista previa</CardTitle>
                                    <CardDescription>Referencia rapida del tema configurado.</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="rounded-lg border border-slate-200 bg-white p-4">
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="h-12 w-12 rounded-md"
                                                style={{ backgroundColor: form['theme.primary_color'] }}
                                            />
                                            <div>
                                                <p className="text-sm font-semibold text-slate-900">
                                                    {form.company_name || 'Farmacias Salud Plus'}
                                                </p>
                                                <p className="text-xs text-slate-500">{form.timezone}</p>
                                            </div>
                                        </div>
                                        <div className="mt-4 grid grid-cols-3 gap-2">
                                            {COLOR_KEYS.map(({ key, label }) => (
                                                <div key={key} className="space-y-1">
                                                    <div className="h-8 rounded-md" style={{ backgroundColor: form[key] }} />
                                                    <p className="text-xs text-slate-500">{label}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <div className="sticky bottom-4 flex justify-end">
                                <Button type="submit" size="lg" disabled={updateMutation.isPending}>
                                    {updateMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Save className="h-4 w-4" />
                                    )}
                                    Guardar cambios
                                </Button>
                            </div>
                        </div>
                    </form>
                )}
            </div>
        </div>
    )
}

function changedSettingsPayload(form: FormState, savedForm: FormState): Record<string, string | null> {
    const payload: Record<string, string | null> = {}
    const keys = Object.keys(form) as Array<keyof FormState>

    for (const key of keys) {
        if (form[key] === savedForm[key]) {
            continue
        }

        payload[key] = key === 'company_name' && form[key] === '' ? null : form[key]
    }

    return payload
}

function ColorField({
    label,
    value,
    onChange,
}: {
    label: string
    value: string
    onChange: (value: string) => void
}) {
    return (
        <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">{label}</label>
            <div className="flex items-center gap-2">
                <input
                    type="color"
                    value={value}
                    onChange={(event) => onChange(event.target.value.toUpperCase())}
                    className="h-10 w-12 rounded-md border border-slate-200 bg-white p-1"
                />
                <Input
                    value={value}
                    onChange={(event) => onChange(event.target.value.toUpperCase())}
                    pattern="^#[0-9A-Fa-f]{6}$"
                    className="font-mono text-sm"
                />
            </div>
        </div>
    )
}
